/**
 * LabBookingRequests — full queue for the lab admin.
 * Lets the lab filter by status and mark requests as contacted / confirmed / completed / cancelled.
 */
import React, { useCallback, useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const STATUSES = [
  { id: "", label: "All" },
  { id: "new", label: "New" },
  { id: "contacted", label: "Contacted" },
  { id: "confirmed", label: "Confirmed" },
  { id: "completed", label: "Completed" },
  { id: "cancelled", label: "Cancelled" },
];

export default function LabBookingRequests() {
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const q = status ? `?status=${status}` : "";
    api.get(`/lab/booking-requests${q}`)
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(apiError(e, "Could not load booking requests")))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const advance = async (id, next) => {
    try {
      await api.patch(`/lab/booking-requests/${id}`, { status: next });
      toast.success(`Marked ${next}`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Update failed"));
    }
  };

  return (
    <DashboardLayout role="lab_admin">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><Calendar className="w-6 h-6 text-brand" /> Booking requests</h1>
            <p className="text-ink/60 text-sm mt-1">Every "Book Now" click from your Smart Reports lands here.</p>
          </div>
          <div className="flex gap-2 flex-wrap" data-testid="br-filters">
            {STATUSES.map((s) => (
              <button
                key={s.id}
                onClick={() => setStatus(s.id)}
                data-testid={`br-filter-${s.id || "all"}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border ${status === s.id ? "bg-ink text-white border-ink" : "bg-white text-ink/70 border-border hover:border-ink/30"}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 space-y-3" data-testid="br-list">
          {loading && <div className="text-ink/50 text-sm">Loading…</div>}
          {!loading && rows.length === 0 && (
            <Card className="p-10 rounded-2xl text-center text-ink/60">
              No booking requests in this view.
            </Card>
          )}
          {rows.map((b) => (
            <Card key={b.id} className="p-5 rounded-2xl" data-testid={`br-card-${b.id}`}>
              <div className="grid md:grid-cols-[1fr_auto] gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading font-semibold text-ink text-lg">{b.patient_name}</div>
                    <span className={`pill text-[10px] ${b.status === "new" ? "pill-borderline" : b.status === "cancelled" ? "pill-abnormal" : "pill-normal"}`}>{b.status}</span>
                    <span className="text-xs text-ink/40">Received {new Date(b.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-2 grid sm:grid-cols-2 gap-y-1 gap-x-4 text-sm">
                    <span className="flex items-center gap-1.5 text-ink/70"><Phone className="w-3.5 h-3.5" /> {b.patient_phone}</span>
                    {b.patient_email && <span className="flex items-center gap-1.5 text-ink/70"><Mail className="w-3.5 h-3.5" /> {b.patient_email}</span>}
                    <span className="flex items-center gap-1.5 text-ink/70 col-span-full"><MapPin className="w-3.5 h-3.5" /> {b.collection_type === "home" ? (b.address || "Home collection") : "Lab visit"}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(b.tests || []).map((t) => (
                      <span key={t} className="px-2 py-0.5 rounded-full text-xs bg-efgh-green/10 text-efgh-green border border-efgh-green/20">{t}</span>
                    ))}
                  </div>
                  {b.preferred_date && (
                    <div className="mt-2 text-xs text-ink/60">Preferred: {b.preferred_date} {b.preferred_time || ""}</div>
                  )}
                  {b.notes && <div className="mt-2 text-xs text-ink/70">Notes: {b.notes}</div>}
                </div>
                <div className="flex flex-col gap-2 items-stretch min-w-[140px]">
                  {b.status === "new" && (
                    <Button size="sm" onClick={() => advance(b.id, "contacted")} className="bg-ink hover:bg-ink/90 text-white" data-testid={`br-mark-contacted-${b.id}`}>Mark contacted</Button>
                  )}
                  {b.status === "contacted" && (
                    <Button size="sm" onClick={() => advance(b.id, "confirmed")} className="bg-efgh-green hover:bg-efgh-green/90 text-white" data-testid={`br-mark-confirmed-${b.id}`}>Mark confirmed</Button>
                  )}
                  {b.status === "confirmed" && (
                    <Button size="sm" onClick={() => advance(b.id, "completed")} className="bg-efgh-green hover:bg-efgh-green/90 text-white" data-testid={`br-mark-completed-${b.id}`}>Mark completed</Button>
                  )}
                  {["new", "contacted", "confirmed"].includes(b.status) && (
                    <Button size="sm" variant="outline" onClick={() => advance(b.id, "cancelled")} data-testid={`br-cancel-${b.id}`}>Cancel</Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
